
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Projects() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'infrastructure', label: 'Infrastructure' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Limpsfield Road, Warlingham',
      category: 'residential',
      location: 'Surrey',
      description: '100 dwellings, including 40% affordable housing, with associated infrastructure, landscaping, and replacement sports facilities.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/8a6d3666ad0de7f392601cf223d368e8.jpeg',
      features: ['100 Units', 'S278', 'S104', 'S38'],
      clickable: true
    },
    {
      id: 2,
      title: 'Coxbridge Farm',
      category: 'infrastructure',
      location: 'Surrey',
      description: 'Section 278 works to facilitate the development of 320 residential units and the connection of the public right of way.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/7aacc6141e8601e9c02443b811c08508.png',
      features: ['S278', 'Right turn lanes', 'PROW', 'Toucan Crossing'],
      clickable: true
    },
    {
      id: 3,
      title: 'Ash',
      category: 'residential',
      location: 'Surrey',
      description: '68 dwellings, including affordable housing, with associated infrastructure and landscaping.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/0dc6638d84260ab17158f3fa3014366d.png',
      features: ['68 Units', 'S278', 'S104', 'S38'],
      clickable: true
    },
    {
      id: 4,
      title: 'Level Strategies',
      category: 'infrastructure',
      location: 'Various locations',
      description: 'Level Strategy drawings for a range of residential schemes, from 30 to 1,000 units. These strategies provide a foundation for land bids and site appraisals, with particular emphasis on value engineering.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/f7db9eb44d937ce532adb5420aeddd4c.png',
      features: ['Optimisation', 'Cost-efficiency', 'Feasibility', 'Flexibility'],
      clickable: false
    },
    {
      id: 5,
      title: 'Earthworks Strategies',
      category: 'infrastructure',
      location: 'Various locations',
      description: 'Cut-and-fill plans using isopachyte triangulation data, balancing volumes to reduce earthworks transport, lower costs, and improve environmental efficiency.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/7be6859578e8433d64cb8c6996979c91.png',
      features: ['Optimisation', 'Cost-efficiency', 'Sustainability', 'Feasibility'],
      clickable: false
    },
    {
      id: 6,
      title: 'Drainage Strategies',
      category: 'infrastructure',
      location: 'Various locations',
      description: 'Drainage strategy plans for residential schemes, optimising water management to minimise flood risk and environmental impact. These strategies provide a framework for sustainable site design and effective water control.',
      image: 'https://static.readdy.ai/image/25bd6a9c0c5cf960313d03e8d1447d98/d50db83e55f456b7fad1ee83e4838d52.png',
      features: ['Optimisation', 'Cost-efficiency', 'Flood-resilience', 'Sustainability'],
      clickable: false
    }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">Featured Projects</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Explore our portfolio of completed engineering projects as an example of our work.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-stone-100 text-gray-700 hover:bg-stone-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div 
              key={project.id} 
              className={`bg-stone-50 rounded-xl overflow-hidden hover:shadow-lg transition-shadow border border-stone-200 ${project.clickable ? 'cursor-pointer' : ''}`}
              onClick={() => project.clickable && navigate(`/project/${project.id}`)}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-medium capitalize">
                    {project.category}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{project.title}</h3>
                <div className="flex items-center text-gray-600 mb-3">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span className="text-sm">{project.location}</span>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">{project.description}</p>
                
                <div className="flex flex-wrap gap-2">
                  {project.features.map((feature, index) => (
                    <span key={index} className="bg-white text-gray-700 px-3 py-1 rounded-full text-xs border border-stone-200">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
